import React, { useCallback } from 'react';
import {
  toBN,
  formatAmountBN,
  VaultUpgradable,
  HedgedLP,
  useInputState,
} from '@sc1/common';
import { useERC20, useCall2, useSend } from '@sc1/common/hooks';
import { Column, InputMax, Box } from '../core';
import { StatusButton } from '../StatusButton';
import type { FlexProps } from 'theme-ui';

interface DepositIntoStrategyProps extends FlexProps {
  vault: VaultUpgradable | undefined;
  strategy: HedgedLP;
  disabled: boolean;
}

export const DepositIntoStrategy = ({
  vault,
  strategy,
  disabled,
  sx,
  ...rest
}: DepositIntoStrategyProps): JSX.Element => {
  const { amountStr, amountBN, setAmountWithMax, setMax } = useInputState();

  const { sendTx, txStatus } = useSend();

  const { res: underlyingAddress } = useCall2(vault?.UNDERLYING);
  const underlying = useERC20(underlyingAddress);
  const { res: underlyingSymbol } = useCall2(underlying?.symbol);
  const { res: dec } = useCall2(underlying?.decimals);

  const { res: maxTvlBN = toBN(0) } = useCall2(strategy?.getMaxTvl);

  const { res: totalFloatBN = toBN(0) } = useCall2(vault?.totalFloat);
  const totalFloatFmt = formatAmountBN(totalFloatBN, dec, false, 2, false);

  const isFull = maxTvlBN.lte(totalFloatBN);

  const maxBN = isFull ? maxTvlBN : totalFloatBN;

  const depositIntoStrategy = useCallback(
    () =>
      vault &&
      strategy?.address &&
      sendTx(vault, 'depositIntoStrategy', [strategy?.address, amountBN]),
    [vault, strategy?.address, sendTx, amountBN]
  );

  return (
    <Column sx={{ flex: 1, ...sx }} {...rest}>
      <Box sx={{ fontSize: 1 }}>
        Float: {totalFloatFmt} {underlyingSymbol}
      </Box>
      <InputMax
        my={2}
        disabled={disabled}
        value={amountStr}
        placeholder="0"
        onMaxClick={() => {
          setMax(maxBN, dec);
        }}
        type="number"
        min="0"
        onChange={(e) => {
          setAmountWithMax(e.target.value, dec, maxBN);
        }}
      />
      <StatusButton
        disabled={disabled}
        onClick={depositIntoStrategy}
        status={txStatus}
      >
        Deposit
      </StatusButton>
    </Column>
  );
};
